<template>
    <b-modal :id="modalId" :title="modalTitle" size="lg" centered scrollable @hidden="onModalHidden">
        <div v-if="!scoreData" class="text-center py-4">
            <i class="fe-alert-circle text-muted" style="font-size: 2rem;"></i>
            <p class="text-muted mt-2 mb-0">無評分數據</p>
        </div>

        <div v-else>
            <!-- 總分顯示 -->
            <div class="mb-3 text-center">
                <h5 class="mb-0">總分: <span class="badge badge-primary">{{ getTotalScore() }} / 100</span></h5>
            </div>

            <!-- 評分表格 -->
            <div class="score-table-wrapper">
                <div class="score-table-grid" v-for="(item, index) in processedTableData" :key="index"
                    :id="`score-table-${index}`">
                    <!-- 表格標題 -->
                    <div class="score-table-label" :class="{
                        'bg-white': index % 2 !== 0,
                        'darkgreen_text': getScoreLevel(parseInt(item.label.score)) === 'darkgreen',
                        'lightgreen_text': getScoreLevel(parseInt(item.label.score)) === 'lightgreen',
                        'orange_text': getScoreLevel(parseInt(item.label.score)) === 'orange',
                        'red_text': getScoreLevel(parseInt(item.label.score)) === 'red'
                    }">
                        <div class="label__text">{{ item.label.text }}</div>
                        <div class="label__score">{{ item.label.score }}</div>
                    </div>
                    <!-- 表格內容 -->
                    <div class="score-table-item" v-for="(phoneItem, phoneIndex) in item.items" :key="phoneIndex">
                        <div class="item__title" :class="{
                            'darkgreen_text': getScoreLevel(parseInt(phoneItem.score)) === 'darkgreen',
                            'lightgreen_text': getScoreLevel(parseInt(phoneItem.score)) === 'lightgreen',
                            'orange_text': getScoreLevel(parseInt(phoneItem.score)) === 'orange',
                            'red_text': getScoreLevel(parseInt(phoneItem.score)) === 'red'
                        }">
                            {{ phoneItem.text }}
                        </div>
                        <div class="item__score" :class="{
                            'darkgreen_text': getScoreLevel(parseInt(phoneItem.score)) === 'darkgreen',
                            'lightgreen_text': getScoreLevel(parseInt(phoneItem.score)) === 'lightgreen',
                            'orange_text': getScoreLevel(parseInt(phoneItem.score)) === 'orange',
                            'red_text': getScoreLevel(parseInt(phoneItem.score)) === 'red'
                        }">
                            {{ phoneItem.score }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template #modal-footer>
            <b-button variant="secondary" @click="$bvModal.hide(modalId)">
                <i class="fe-x"></i> 關閉
            </b-button>
        </template>
    </b-modal>
</template>

<script>
export default {
    name: 'ScoreTableModal',
    props: {
        modalId: {
            type: String,
            required: true
        },
        modalTitle: {
            type: String,
            default: '評分詳情'
        },
        scoreData: {
            type: Object,
            default: null
        }
    },
    data () {
        return {
            processedTableData: [],

            // 音素對應表 - 將API音素替換為正確顯示的音素
            phoneMapping: {
                // 英語元音音素 (ARPABET -> IPA)
                'aa': 'ɑ', 'ae': 'æ', 'ah': 'ʌ', 'ao': 'ɔ', 'ay': 'aɪ',
                'eh': 'ɛ', 'ey': 'eɪ', 'ih': 'ɪ', 'iy': 'i', 'ow': 'oʊ',
                'oy': 'ɔɪ', 'uh': 'ʊ', 'uw': 'u', 'er': 'ɜr', 'ax': 'ə',
                'aw': 'aʊ', 'el': 'əl', 'em': 'əm', 'en': 'ən', 'eng': 'əŋ',

                // 英語輔音音素 (ARPABET -> IPA)
                'b': 'b', 'd': 'd', 'f': 'f', 'g': 'ɡ', 'h': 'h',
                'j': 'dʒ', 'k': 'k', 'l': 'l', 'm': 'm', 'n': 'n',
                'ng': 'ŋ', 'p': 'p', 'r': 'r', 's': 's', 'sh': 'ʃ',
                't': 't', 'th': 'θ', 'dh': 'ð', 'v': 'v', 'w': 'w',
                'y': 'j', 'z': 'z', 'zh': 'ʒ', 'ch': 'tʃ',

                // 法語音素 (ARPABET -> IPA)
                'oon': 'ɔ̃', 'ehn': 'ɛ̃', 'agn': 'ɑ̃', 'oen': 'œ̃',
                'io': 'jo', 'uo': 'wo', 'ad': 'a', 'eu': 'ø',

                // 其他常見音素
                'dx': 'ɾ', 'hh': 'h', 'nx': 'ɾ̃', 'q': 'ʔ', 'ts': 'ts', 'dz': 'dz',
                'yi': 'ji', 'rw': 'ʁ'
            }
        }
    },
    watch: {
        scoreData: {
            handler (newData) {
                if (newData) {
                    this.processTableData(newData)
                }
            },
            immediate: true
        }
    },
    methods: {
        getScoreLevel (score) {
            if (score === null || score === undefined || isNaN(score)) {
                return 'red'
            }
            if (score >= 90) {
                return 'darkgreen'
            } else if (score >= 80) {
                return 'lightgreen'
            } else if (score >= 60) {
                return 'orange'
            }
            return 'red'
        },

        getPhoneDisplayText (phoneCode, stressLevel = null) {
            // 直接查找對應
            if (this.phoneMapping[phoneCode]) {
                let ipaSymbol = this.phoneMapping[phoneCode]

                // 如果有重音信息，添加重音標記
                if (stressLevel !== null && stressLevel !== undefined) {
                    // 根據重音級別添加適當的標記
                    if (stressLevel === 1) {
                        ipaSymbol += 'ˈ' // 主重音
                    } else if (stressLevel === 2) {
                        ipaSymbol += 'ˌ' // 次重音
                    }
                    // stressLevel === 0 表示無重音，不添加標記
                }

                return ipaSymbol
            }

            // 如果沒有找到對應，返回原始音素代碼
            return phoneCode
        },

        async processTableData (voiceAnalysisData) {
            try {
                this.processedTableData = []

                if (!voiceAnalysisData || !voiceAnalysisData.text_score || !voiceAnalysisData.text_score.word_score_list) {
                    return
                }

                await voiceAnalysisData.text_score.word_score_list.forEach((word) => {
                    let wordItems = []

                    // 處理每個音素
                    word.phone_score_list.forEach((phoneScore) => {
                        let phoneText = this.getPhoneDisplayText(phoneScore.phone, phoneScore.stress_level)
                        wordItems.push({
                            text: phoneText,
                            score: this.formatNumber(phoneScore.quality_score, 2)
                        })
                    })

                    this.processedTableData.push({
                        label: {
                            text: word.word,
                            score: word.quality_score
                        },
                        items: wordItems
                    })
                })

                // 等待DOM更新後調整表格佈局
                this.$nextTick(() => {
                    this.adjustTableLayout()
                })
            } catch (error) {
                console.error('處理表格數據時發生錯誤:', error)
            }
        },

        getTotalScore () {
            if (!this.scoreData || !this.scoreData.text_score || !this.scoreData.text_score.speechace_score) {
                return 0
            }
            return this.scoreData.text_score.speechace_score.pronunciation || 0
        },

        formatNumber (num, decimals = 2) {
            return parseFloat(num).toFixed(decimals)
        },

        adjustTableLayout () {
            try {
                let isMobile = window.innerWidth < 520

                this.processedTableData.forEach((item, index) => {
                    const label = document.querySelector(`#score-table-${index} .score-table-label`)
                    const wrapper = document.querySelector('.score-table-wrapper')

                    if (label && wrapper) {
                        const wrapperWidth = wrapper.offsetWidth
                        let perRow, rowCount

                        if (isMobile) {
                            perRow = Math.floor((wrapperWidth - 118) / 64)
                        } else {
                            perRow = Math.floor((wrapperWidth - 128) / 108)
                        }

                        rowCount = Math.ceil(item.items.length / perRow)
                        label.style.gridRow = `1 / span ${rowCount}`
                    }
                })
            } catch (error) {
                console.error('調整表格佈局時發生錯誤:', error)
            }
        },

        onModalHidden () {
            this.processedTableData = []
        }
    }
}
</script>

<style lang="scss" scoped>
.score-table-wrapper {
    margin: 12px 0;
    padding-bottom: 15px;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    overflow: hidden;

    .score-table-grid {
        display: grid;
        grid-template-columns: 129px repeat(auto-fill, 108px);
        grid-auto-rows: 100px;
        grid-auto-flow: row;
        margin-bottom: 0;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .score-table-label {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        gap: 4px;
        background: #e3f2fd;
        border: 1px solid #bbdefb;
        font-weight: 600;
        font-size: 16px;
        color: #1976d2;

        &.bg-white {
            background: #f8f9fa;
            color: #495057;
        }
    }

    .score-table-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border: 1px solid #dee2e6;
        background: #fff;

        .item__title {
            width: 100%;
            border-bottom: 1px solid #dee2e6;
            font-size: 16px;
            font-weight: 600;
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 1;
            background: #f8f9fa;
        }

        .item__score {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 1;
            font-size: 16px;
            font-weight: 600;
            background: #fff;
        }
    }
}

// 分數顏色樣式
.darkgreen_text {
    color: #009900 !important;
}

.lightgreen_text {
    color: #66CC66 !important;
}

.orange_text {
    color: #FF9900 !important;
}

.red_text {
    color: #FF3333 !important;
}

// 響應式設計
@media (max-width: 520px) {
    .score-table-wrapper {
        .score-table-grid {
            grid-template-columns: 118px repeat(auto-fill, 64px);
            grid-auto-flow: row;
        }
    }
}
</style>
